import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { codigoDoVideo, enderecoDoPlayer, miniatura } from './youtube';

/**
 * Lista de vídeos, no molde dos folders:
 *
 *  - azul: faixa de ponta a ponta com a padronagem da marca, player grande e
 *    a lista ao lado (no celular, a lista vem embaixo);
 *  - branco: cartão na coluna, player em cima e as miniaturas numa grade.
 *
 * O player do YouTube só entra no clique, pelo youtube-nocookie: antes, a
 * miniatura e o botão de play. Escolher outro vídeo na lista troca o player
 * (se já estava tocando, segue tocando o novo).
 */

export const videosDe = (data) =>
  (data.videos || [])
    .map((v, i) => ({
      id: v?.['@id'] || `v${i}`,
      codigo: codigoDoVideo(v?.url),
      titulo: (v?.titulo || '').trim(),
    }))
    .filter((v) => v.codigo);

// A miniatura grande (maxres) não existe em todo vídeo: cai na média.
const trocarParaMedia = (codigo) => (e) => {
  const reserva = miniatura(codigo, 'hqdefault');
  if (e.currentTarget.src !== reserva) e.currentTarget.src = reserva;
};

const VideosView = ({ data = {}, vazio }) => {
  const videos = videosDe(data);
  const [atual, setAtual] = useState(0);
  const [tocando, setTocando] = useState(false);
  if (!videos.length) return vazio || null;
  const i = Math.min(atual, videos.length - 1);
  const v = videos[i];
  const fundo = data.fundo === 'azul' ? 'azul' : 'branco';
  const Nivel = data.nivel === 'h3' ? 'h3' : 'h2';
  const etiqueta = (data.etiqueta || '').trim();
  const contagem = data.contagem !== false && videos.length > 1;
  const nome = v.titulo || `Vídeo ${i + 1}`;

  return (
    <section className={`ag-videos ag-videos--${fundo}`}>
      <div className="ag-videos__inner">
        {etiqueta && (
          <span className="ag-videos__etiqueta">
            {etiqueta}
            {contagem ? ` · ${videos.length}` : ''}
          </span>
        )}
        {(data.titulo || '').trim() && (
          <Nivel className="ag-videos__titulo">{data.titulo.trim()}</Nivel>
        )}
        {(data.subtitulo || '').trim() && (
          <p className="ag-videos__sub">{data.subtitulo.trim()}</p>
        )}
        <div
          className={`ag-videos__corpo${
            videos.length > 1 ? '' : ' ag-videos__corpo--um'
          }`}
        >
          <div className="ag-videos__player">
            {tocando ? (
              <div className="ag-videos__quadro">
                <iframe
                  key={v.codigo}
                  src={enderecoDoPlayer(v.codigo)}
                  title={nome}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              </div>
            ) : (
              <button
                type="button"
                className="ag-videos__capa"
                aria-label={`Assistir: ${nome}`}
                onClick={() => setTocando(true)}
              >
                <img
                  key={v.codigo}
                  src={miniatura(v.codigo)}
                  alt=""
                  loading="lazy"
                  onError={trocarParaMedia(v.codigo)}
                />
                <span className="ag-videos__play" aria-hidden="true" />
              </button>
            )}
            <p className="ag-videos__agora">
              {videos.length > 1 && (
                <span>
                  Vídeo {i + 1} de {videos.length}
                </span>
              )}
              {nome}
            </p>
          </div>
          {videos.length > 1 && (
            <ol className="ag-videos__lista">
              {videos.map((x, n) => (
                <li key={x.id}>
                  <button
                    type="button"
                    className="ag-videos__item"
                    aria-current={n === i ? 'true' : undefined}
                    onClick={() => setAtual(n)}
                  >
                    <img
                      src={miniatura(x.codigo, 'mqdefault')}
                      alt=""
                      loading="lazy"
                    />
                    <span>
                      <span className="ag-videos__num" aria-hidden="true">
                        {String(n + 1).padStart(2, '0')}
                      </span>
                      {x.titulo || `Vídeo ${n + 1}`}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
};

VideosView.propTypes = {
  data: PropTypes.shape({
    fundo: PropTypes.string,
    etiqueta: PropTypes.string,
    titulo: PropTypes.string,
    subtitulo: PropTypes.string,
    nivel: PropTypes.string,
    contagem: PropTypes.bool,
    videos: PropTypes.array,
  }),
  vazio: PropTypes.node,
};

export default VideosView;
